import { useSyncExternalStore } from "react";

/** How often an open cabinet asks the server which build it serves. */
export const CHECK_INTERVAL_MS = 60_000;

export const UPDATE_NOTICE_TEXT_KEY = "mainLayout.header.updateNotice";

let found = false;
const listeners = new Set<() => void>();

/** Build id baked into this bundle by scripts/build.js; none in `npm start`. */
export const getOwnBuildId = (): string | undefined =>
	process.env.REACT_APP_BUILD_ID || undefined;

export const isNewBuildFound = () => found;

/** Once found, stays until the page reloads: only F5 brings the new build. */
export const markNewBuild = () => {
	if (found) return;
	found = true;
	listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

export const useNewBuildFound = () =>
	useSyncExternalStore(subscribe, isNewBuildFound);

/** `build` of version.json, or null when the answer is not usable. */
export const parseServedBuildId = (body: unknown): string | null => {
	if (!body || typeof body !== "object") return null;
	const { build } = body as { build?: unknown };
	return typeof build === "string" && build ? build : null;
};

/**
 * Asks for version.json and marks a new build when its id differs from ours.
 * A failed request is not an error here: the connection indicator shows the
 * network, the next check simply tries again.
 */
export const checkServedBuild = async (ownBuildId: string) => {
	if (found) return;
	try {
		const response = await fetch(
			`${process.env.PUBLIC_URL || ""}/version.json?t=${Date.now()}`,
			{ cache: "no-store" },
		);
		if (!response.ok) return;
		const served = parseServedBuildId(await response.json());
		if (served && served !== ownBuildId) markNewBuild();
	} catch {
		// Offline or not JSON: wait for the next check.
	}
};

/** A lazy chunk (JS or CSS) that failed to load: gone after a deploy. */
export const isChunkLoadError = (error: unknown) =>
	error instanceof Error &&
	(error.name === "ChunkLoadError" ||
		/Loading (CSS )?chunk .+ failed/.test(error.message));

/** Test only: back to «nothing found». */
export const resetNewBuildForTest = () => {
	found = false;
};
